import { belongsTo, Factory, Model, hasMany } from 'miragejs'
import type { ModelDefinition } from 'miragejs/-types'
import type { MirageServer, MockServer } from '@@/api/mock-server/server'
import { rand, randPastDate } from '@ngneat/falso'
import type { SchemaAttributes } from '@@/api/types'
import { generateFinanceAmount } from '@@/api/mock-server/utils.js'
import { seedInvoiceData } from '@@/api/mock-server/Invoice.js'
import type { InvoicePaymentData } from '@/composables/api/Invoice.js'
import { roundEven, toNumber } from '@/utils/number.js'

type PrepaymentData = SchemaAttributes<'ClientStatementPrepayment'>

export const model = Model.extend({
  client: belongsTo('Client'),
  department: belongsTo('Department'),
  paymentMethod: belongsTo('PaymentMethod'),
  payment: hasMany('InvoicePayment'),
  clientStatement: belongsTo('ClientStatement'),
}) as ModelDefinition<InvoicePaymentData>

export const factory = Factory.extend<PrepaymentData>({
  paid() {
    return roundEven(toNumber(generateFinanceAmount({ min: 200, max: 500 })), 2)
  },
  date_added() {
    return randPastDate().toISOString()
  },
  current_refunded_amount() {
    return roundEven(
      rand([
        0,
        toNumber(generateFinanceAmount({ min: 0, max: toNumber(this.paid) })),
        toNumber(this.paid),
      ]),
      2,
    )
  },
  current_used_amount() {
    return roundEven(
      rand([
        toNumber(this.paid) - toNumber(this.current_refunded_amount),
        toNumber(
          generateFinanceAmount({
            min: 0,
            max: toNumber(this.paid) - toNumber(this.current_refunded_amount),
          }),
        ),
      ]),
      2,
    )
  },
  current_unused_amount() {
    return (
      toNumber(this.paid) -
      toNumber(this.current_used_amount) -
      toNumber(this.current_refunded_amount)
    )
  },
  used_amount_at_end_date() {
    return toNumber(this.current_used_amount)
  },
  unused_amount_at_end_date() {
    return toNumber(this.current_unused_amount)
  },
  refunded_amount_at_end_date() {
    return toNumber(this.current_refunded_amount)
  },
})

export function routes(server: MockServer): void {
  // Ensure a prepayment exists for the requested id and seed invoices for the client
  server.get(
    '/prepayments/:id',
    function (this: MirageServer, schema, request) {
      const { id } = request.params
      const { client_id: clientIdParam } = request.queryParams as {
        client_id?: string
      }

      const effectiveClientId = String(clientIdParam ?? '')

      // Ensure essential related data
      const department =
        schema.first('department') || server.create('department')

      let client = schema.find('client', effectiveClientId)
      if (!client) {
        client = server.create('client', { id: effectiveClientId })
      }

      const existingPatients = schema
        .all('patient')
        .models.filter((p) => String(p.clientId) === effectiveClientId)
      if (existingPatients.length === 0 && client) {
        server.createList('patient', 1, { client })
      }

      // Ensure prepayment exists for the requested id
      let prepayment = schema.find('prepayment', id)
      if (!prepayment) {
        prepayment = server.create('prepayment', { id, client, department })
      }

      const invoicesForClient = schema
        .all('invoice')
        .models.filter((inv) => String(inv.clientId) === effectiveClientId)

      // If this client has no invoices, seed general test data (for all test clients)
      // and create specific invoices for this client
      if (invoicesForClient.length === 0) {
        seedInvoiceData(server)

        server.create('invoice', 'withRows', 'withNoPayments', {
          department,
          client,
          credit_note: false,
        })

        server.create(
          'invoice',
          'withFinalization',
          'withRows',
          'withPartialPayment',
          {
            department,
            client,
            credit_note: false,
          },
        )
      }

      return prepayment
    },
  )
}
